import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../ui/input';
import Label from '../ui/label';
import { sendSupportEmail } from '../../redux/api/notificationAPI';

function ContactSupportModal({ showModal, onClose, site }) {
  const { t } = useTranslation('common');

  // Contact Modal State
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [formErrors, setFormErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (formErrors[name]) setFormErrors((p) => ({ ...p, [name]: '' }));
  };

  const resetModal = () => {
    setForm({ name: '', email: '', message: '' });
    setFormErrors({});
    setApiError(null);
    setSent(false);
  };

  const closeModal = () => {
    resetModal();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setApiError(null);
    setFormErrors({});

    const errors = {};

    // Validate Name
    if (!form.name || !form.name.trim()) {
      errors.name = t('enter_name') || 'Enter your name';
    }

    // Validate Email
    if (!form.email || !form.email.trim()) {
      errors.email = t('enter_email') || 'Enter Email Address';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        errors.email = t('invalid_email') || 'Enter a valid email address';
      }
    }

    // Validate Message
    if (!form.message || !form.message.trim()) {
      errors.message = t('enter_message') || 'Enter Message';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      message: form.message.trim(),
      site_id: site || null,
    };

    const { error } = await sendSupportEmail(payload);

    if (error) {
      setApiError(t('contact_failed') || 'Failed to send message. Please try again.');
    } else {
      setSent(true);
      setTimeout(closeModal, 2200);
    }

    setLoading(false);
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <form onSubmit={submit}>
        <div className="min-w-[600px] max-w-3xl w-auto m-2 bg-white max-md:p-6 p-10 rounded-3xl shadow-lg overflow-y-auto">
          <p className="flex justify-center col-span-full text-2xl text-[#4C4C66] font-bold mb-8 bg-white">
            {t('contact_us') || 'Contact Support'}
          </p>

          {sent ? (
            <div className="col-span-full text-center py-16">
              <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-green-100">
                <svg className="h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mt-6 text-xl font-semibold text-[#4C4C66]">{t('message_sent') || 'Message Sent'}</h3>
              <p className="mt-3 text-base text-gray-600">{t('support_will_reply') || "We'll get back to you as soon as possible."}</p>
            </div>
          ) : (
            <div className="grid gap-6 max-md:grid-cols-1 grid-cols-2">
              <Input
                required
                label={t('name') || 'Your Name'}
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t('enter_name') || 'Enter your name'}
                error={formErrors.name}
              />
              <Input
                required
                label={t('email') || 'Email'}
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder={t('enter_email') || 'Enter Email Address'}
                error={formErrors.email}
              />
              <div className="col-span-full">
                <Label setValue={t('message') || 'Message'} />
                <textarea
                  required
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={handleChange}
                  placeholder={t('enter_message') || 'Enter Message'}
                  className={`
                    mt-1 block w-full rounded-lg border 
                    ${formErrors.message ? 'border-red-500' : 'border-gray-300'}
                    bg-gray-50 px-4 py-3 text-base text-gray-700 placeholder-gray-400
                    focus:outline-none focus:ring-2 focus:ring-[#0071BC] focus:border-[#0071BC]
                    resize-none
                  `}
                />
                {formErrors.message && <p className="mt-1 text-sm text-red-600">{formErrors.message}</p>}
              </div>
              {apiError && (
                <div className="col-span-full text-center">
                  <p className="text-sm text-red-600">{apiError}</p>
                </div>
              )}
              <div className="col-span-full flex justify-end mt-8 space-x-5">
                <button
                  type="button"
                  onClick={closeModal}
                  className="min-w-[140px] h-12 px-6 text-base font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
                >
                  {t('cancel') || 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="min-w-[160px] h-12 px-6 text-base font-bold text-white bg-[#0071BC] rounded-lg hover:bg-[#005a9c] focus:outline-none focus:ring-2 focus:ring-[#0071BC] disabled:opacity-70 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      {t('sending') || 'Sending...'}
                    </>
                  ) : (
                    t('send') || 'Send Message'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default ContactSupportModal;